import React, { useState, useEffect } from 'react';
import { get, post, put } from '../utils/api';
import { useAuth } from '../context/AuthContext';

const ROLES = [
  { value:'super_admin', label:'Managing Director' },
  { value:'sales_manager', label:'Customer Experience & Sales Manager' },
  { value:'production_team_lead', label:'Production Team Lead' },
  { value:'tailor', label:'Tailor' },
  { value:'production_assistant', label:'Production Assistant' },
  { value:'inventory_officer', label:'Inventory & Materials Officer' },
  { value:'procurement_logistics_officer', label:'Procurement & Logistics Officer' },
  { value:'front_desk_officer', label:'Front Desk Officer' },
  { value:'content_sales', label:'Brand & Content Officer' },
  { value:'driver', label:'Driver' },
  { value:'auditor', label:'Auditor' },
];

export default function JobDescriptions() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';
  const [jds, setJds] = useState([]);
  const [myJd, setMyJd] = useState(null);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showView, setShowView] = useState(false);
  const [activeTab, setActiveTab] = useState('mine');
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    role_name:'', job_title:'', department:'', reports_to:'',
    purpose:'', responsibilities:'', permissions_summary:'',
    restrictions:'', kpis:'', daily_responsibilities:'',
    weekly_responsibilities:'', monthly_responsibilities:'',
    required_reports:'', effective_date:''
  });

  const load = () => {
    setLoading(true);
    Promise.all([
      get('/job-descriptions'),
      get('/job-descriptions/mine')
    ]).then(([all, mine]) => {
      setJds(all.data||[]);
      setMyJd(mine.data);
    }).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const saveJd = async () => {
    if (!form.role_name || !form.job_title) { alert('Role and title required'); return; }
    setSaving(true);
    try {
      if (selected && selected.id) {
        await put('/job-descriptions/'+selected.id, form);
      } else {
        await post('/job-descriptions', form);
      }
      setShowForm(false);
      setSelected(null);
      load();
    } catch(err) { alert(err.message); }
    finally { setSaving(false); }
  };

  const publishJd = async (id) => {
    if (!window.confirm('Publish this JD? All staff with this role will be notified.')) return;
    try {
      await put('/job-descriptions/'+id+'/publish', {});
      load();
      alert('JD published and staff notified');
    } catch(err) { alert(err.message); }
  };

  const acknowledgeJd = async (id) => {
    try {
      await post('/job-descriptions/'+id+'/acknowledge', {});
      load();
      alert('Job Description acknowledged successfully');
    } catch(err) { alert(err.message); }
  };

  const TextArea = ({label, field, rows=3}) => (
    <div className="form-group">
      <label className="form-label">{label}</label>
      <textarea className="form-control" rows={rows} value={form[field]||''}
        onChange={e=>setForm({...form,[field]:e.target.value})}
        placeholder={label+'...'} />
    </div>
  );

  return (
    <div>
      <div className="flex-between mb-lg">
        <div>
          <h2 className="page-title">📋 Job Descriptions</h2>
          <p className="text-muted text-sm" style={{marginTop:4}}>
            Role responsibilities, expectations and boundaries
          </p>
        </div>
        {isSuperAdmin && (
          <button className="btn btn-primary" onClick={()=>{
            setSelected(null);
            setForm({role_name:'',job_title:'',department:'',reports_to:'',
              purpose:'',responsibilities:'',permissions_summary:'',
              restrictions:'',kpis:'',daily_responsibilities:'',
              weekly_responsibilities:'',monthly_responsibilities:'',
              required_reports:'',effective_date:''});
            setShowForm(true);
          }}>+ New JD</button>
        )}
      </div>

      {/* Tabs */}
      <div style={{display:'flex',gap:6,marginBottom:20,borderBottom:'1px solid var(--border-light)',paddingBottom:12}}>
        {[['mine','My JD'],['all','All JDs']].map(([key,label])=>(
          <button key={key} className={'btn btn-sm '+(activeTab===key?'btn-primary':'btn-ghost')}
            onClick={()=>setActiveTab(key)}>{label}</button>
        ))}
      </div>

      {loading ? <div className="spinner"></div> : (
        <>
          {/* MY JD */}
          {activeTab==='mine' && (
            <div>
              {!myJd ? (
                <div className="empty-state">
                  <h3>No Job Description yet</h3>
                  <p>Your job description has not been published yet. Check back soon.</p>
                </div>
              ) : (
                <div style={{maxWidth:700}}>
                  <div style={{background:'#3B2A21',borderRadius:'12px 12px 0 0',padding:'20px 24px',
                    display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                    <div>
                      <h3 style={{color:'#A8893D',fontFamily:'Cormorant Garamond,serif',
                        fontSize:'1.4rem',margin:0}}>{myJd.job_title}</h3>
                      <div style={{color:'rgba(242,239,231,.6)',fontSize:12,marginTop:4}}>
                        {myJd.department} · Version {myJd.version} · Effective: {myJd.effective_date||'Not set'}
                      </div>
                    </div>
                    {myJd.my_ack ? (
                      <div style={{background:'#556B2F',padding:'6px 14px',borderRadius:20,
                        fontSize:11,color:'#F2EFE7',fontWeight:600}}>
                        ✓ Acknowledged
                      </div>
                    ) : (
                      <button className="btn btn-primary btn-sm"
                        onClick={()=>acknowledgeJd(myJd.id)}>
                        Acknowledge JD
                      </button>
                    )}
                  </div>

                  <div style={{background:'#fff',border:'1px solid var(--border-light)',
                    borderTop:'none',borderRadius:'0 0 12px 12px',padding:24}}>
                    {[
                      ['Purpose', myJd.purpose],
                      ['Reports To', myJd.reports_to],
                      ['Responsibilities', myJd.responsibilities],
                      ['Permissions', myJd.permissions_summary],
                      ['Restrictions', myJd.restrictions],
                      ['KPIs', myJd.kpis],
                      ['Daily Responsibilities', myJd.daily_responsibilities],
                      ['Weekly Responsibilities', myJd.weekly_responsibilities],
                      ['Monthly Responsibilities', myJd.monthly_responsibilities],
                      ['Required Reports', myJd.required_reports],
                    ].filter(([,val])=>val).map(([label, val])=>(
                      <div key={label} style={{marginBottom:20}}>
                        <div style={{fontSize:10,fontWeight:600,color:'#556B2F',
                          textTransform:'uppercase',letterSpacing:'.1em',marginBottom:6}}>
                          {label}
                        </div>
                        <div style={{fontSize:13,color:'#3B2A21',lineHeight:1.7,
                          whiteSpace:'pre-wrap'}}>{val}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ALL JDs */}
          {activeTab==='all' && (
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))',gap:12}}>
              {jds.length === 0 ? (
                <div className="empty-state" style={{gridColumn:'1/-1'}}>
                  <h3>No job descriptions yet</h3>
                  {isSuperAdmin && <p>Click + New JD to create the first one.</p>}
                </div>
              ) : jds.map(jd => (
                <div key={jd.id} style={{background:'#fff',border:'1px solid var(--border-light)',
                  borderRadius:10,overflow:'hidden',cursor:'pointer'}}
                  onClick={()=>{setSelected(jd);setShowView(true);}}>  
                  <div style={{background:'#3B2A21',padding:'12px 16px'}}>
                    <div style={{fontSize:13,fontWeight:600,color:'#A8893D'}}>{jd.job_title}</div>
                    <div style={{fontSize:11,color:'rgba(242,239,231,.6)',marginTop:2}}>{jd.department}</div>
                  </div>
                  <div style={{padding:'12px 16px'}}>
                    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8}}>
                      <span style={{fontSize:9,fontWeight:700,padding:'2px 8px',borderRadius:20,
                        textTransform:'uppercase',
                        background:jd.status==='published'?'#E8F5EB':'#FFF3DC',
                        color:jd.status==='published'?'#2E6B3A':'#8B5E0A'}}>
                        {jd.status}
                      </span>
                      <span style={{fontSize:10,color:'#8A8A8A'}}>v{jd.version}</span>
                    </div>
                    <div style={{fontSize:11,color:'#8A8A8A'}}>
                      {jd.ack_count} acknowledgement{jd.ack_count!==1?'s':''}
                    </div>
                    {isSuperAdmin && (
                      <div style={{display:'flex',gap:6,marginTop:10}}>
                        <button className="btn btn-outline btn-sm" style={{fontSize:10}}
                          onClick={e=>{e.stopPropagation();
                            setForm({...jd});setSelected(jd);setShowForm(true);}}>
                          Edit
                        </button>
                        {jd.status !== 'published' && (
                          <button className="btn btn-primary btn-sm" style={{fontSize:10}}
                            onClick={e=>{e.stopPropagation();publishJd(jd.id);}}>
                            Publish
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* JD View Modal */}
      {showView && selected && (
        <div className="modal-overlay">
          <div className="modal" style={{maxWidth:700}}>
            <div className="modal-hdr" style={{background:'#3B2A21',borderRadius:'10px 10px 0 0'}}>
              <div>
                <div style={{color:'#A8893D',fontFamily:'Georgia,serif',fontSize:'1.2rem',fontWeight:700}}>{selected.job_title}</div>
                <div style={{color:'rgba(242,239,231,.6)',fontSize:11,marginTop:2}}>{selected.department} · Version {selected.version} · {selected.status}</div>
              </div>
              <button className="btn btn-ghost btn-sm" style={{color:'#F2EFE7'}} onClick={()=>setShowView(false)}>✕</button>
            </div>
            <div className="modal-body" style={{maxHeight:'75vh',overflowY:'auto'}}>
              {[
                ['Purpose', selected.purpose],
                ['Reports To', selected.reports_to],
                ['Responsibilities', selected.responsibilities],
                ['Permissions', selected.permissions_summary],
                ['Restrictions', selected.restrictions],
                ['KPIs', selected.kpis],
                ['Daily Responsibilities', selected.daily_responsibilities],
                ['Weekly Responsibilities', selected.weekly_responsibilities],
                ['Monthly Responsibilities', selected.monthly_responsibilities],
                ['Required Reports', selected.required_reports],
              ].filter(([,val])=>val).map(([label,val])=>(
                <div key={label} style={{marginBottom:20}}>
                  <div style={{fontSize:10,fontWeight:700,color:'#556B2F',textTransform:'uppercase',letterSpacing:'.1em',marginBottom:6}}>{label}</div>
                  <div style={{fontSize:13,color:'#3B2A21',lineHeight:1.8,whiteSpace:'pre-wrap'}}>{val}</div>
                </div>
              ))}
              {selected.effective_date && (
                <div style={{fontSize:11,color:'#8A8A8A',marginTop:8}}>Effective date: {selected.effective_date}</div>
              )}
            </div>
            <div className="modal-ftr">
              <button className="btn btn-outline" onClick={()=>setShowView(false)}>Close</button>
              {isSuperAdmin && (
                <button className="btn btn-primary" onClick={()=>{
                  setForm({...selected});
                  setShowView(false);
                  setShowForm(true);
                }}>Edit JD</button>
              )}
            </div>
          </div>
        </div>
      )}
      {/* JD Form Modal */}
      {showForm && isSuperAdmin && (
        <div className="modal-overlay">
          <div className="modal" style={{maxWidth:640}}>
            <div className="modal-hdr">
              <span className="modal-title">{selected?.id ? 'Edit' : 'New'} Job Description</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setShowForm(false)}>X</button>
            </div>
            <div className="modal-body" style={{maxHeight:'70vh',overflowY:'auto'}}>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Role *</label>
                  <select className="form-control" value={form.role_name}
                    onChange={e=>setForm({...form,role_name:e.target.value,
                      job_title:ROLES.find(r=>r.value===e.target.value)?.label||''})}>
                    <option value="">Select role...</option>
                    {ROLES.map(r=><option key={r.value} value={r.value}>{r.label}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Job Title *</label>
                  <input className="form-control" value={form.job_title}
                    onChange={e=>setForm({...form,job_title:e.target.value})} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Department</label>
                  <input className="form-control" value={form.department}
                    onChange={e=>setForm({...form,department:e.target.value})} />
                </div>
                <div className="form-group">
                  <label className="form-label">Reports To</label>
                  <input className="form-control" value={form.reports_to}
                    onChange={e=>setForm({...form,reports_to:e.target.value})} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Effective Date</label>
                <input type="date" className="form-control" value={form.effective_date}
                  onChange={e=>setForm({...form,effective_date:e.target.value})} />
              </div>
              <TextArea label="Role Purpose" field="purpose" rows={3} />
              <TextArea label="Responsibilities" field="responsibilities" rows={5} />
              <TextArea label="Permissions Summary" field="permissions_summary" rows={4} />
              <TextArea label="Restrictions (Cannot do)" field="restrictions" rows={4} />
              <TextArea label="KPIs" field="kpis" rows={3} />
              <TextArea label="Daily Responsibilities" field="daily_responsibilities" rows={3} />
              <TextArea label="Weekly Responsibilities" field="weekly_responsibilities" rows={3} />
              <TextArea label="Monthly Responsibilities" field="monthly_responsibilities" rows={3} />
              <TextArea label="Required Reports" field="required_reports" rows={3} />
            </div>
            <div className="modal-ftr">
              <button className="btn btn-outline" onClick={()=>setShowForm(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveJd} disabled={saving}>
                {saving?'Saving...':'Save JD'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
